import type { DefaultTheme } from "styled-components";

import { BLUE, DARK_BLUE, GREEN, ORANGE, PURPLE, RED, TEAL, WHITE, YELLOW } from "./colors";
import { semantic } from "./semantic";

// GREY BLUES
const DARK_GREY_BLUE = "#384a5f";
const GREY_BLUE = "#567291";
const LIGHT_GREY_BLUE = "#6585ab";

// REDS
const TOMATO = "#e32926";
const SALMON = "#fd7b7b";
const LIGHT_RED = "#ffe6e6";
const ERROR = "#ff5959";

// GREENS
const FEELING_GREEN = "#57cb75";
const LIGHT_GREEN = "#d5f2dc";

// PURPLES
const PLUM = "#9b4d86";

// BLUES
const ELECTRIC_BLUE = "#1274e3";
const DARK_SKY_BLUE = "#4a90e2";
const LIGHT_SKY_BLUE = "#479DFF";
const LIGHT_BLUE = "#daecff";
const ICE_BLUE = "#f7fbff";
const PAINFUL_BLUE = "#00c8ff";
const VOICE_CALL_CONTAINER = "#273546";

// GRAYSCALE
const BLACK = "#000000";
export const SLATE_GREY = "#676d75";
const WARM_GREY = "#9b9b9b";
const PINKISH_GREY = "#c2c2c2";
const SILVER = "#dadfe2";
const LIGHT_GREY1 = "#e8e8e8";
const LIGHT_GREY2 = "#f2f2f2";
const LIGHT_GREY3 = "#f5f8fc";
const LIGHT_GREY4 = "#f8f8f8";
const LIGHT_GREY5 = "#ebebeb";
const LIGHT_GREY6 = "#f5f5f5";
const LIGHT_GREY7 = "#fbfbfb";
const IRON_GREY = "#8495a8";
const CONCRETE_GREY = "#e1e8ef";
const GUNMETAL_GREY = "#8d9bad";
const DARKER_GREY = "#1c2126";

// GREYS
const NEW_GREY = "#A3AAB3";
const LIGHTEST_GREY = "#F8DBCA";
const DISABLED_GREY = "#F0F0F0";

// AVATAR COLORS
const COLOR_SWATCHES = [SALMON, "#EE508A", "#F5C674", FEELING_GREEN, "#53BBB4", PLUM, "#52E1FF", "#2F6DCA", "#4E53A4"];

const PAIN_LOCATION_COLORS = {
  knee: "#8A3CAF",
  hand: "#E9A727",
  back: "#3CAFAF",
  exclusion: "#F0757C",
  hip: DARK_GREY_BLUE,
  shoulder: "#8A0035",
  neck: "#2D6CC0",
};

// FONT WEIGHT
const FONT_WEIGHT_LIGHT = "300";
const FONT_WEIGHT_REGULAR = "400";
const FONT_WEIGHT_MEDIUM = "500";
const FONT_WEIGHT_BOLD = "700";

// BREAKPOINTS
const BREAKPOINT = "767";
const BREAKPOINT_MOBILE = "480";
const BREAKPOINT_LARGE = "992";
const BREAKPOINT_LIB = "550";

export const theme: DefaultTheme = {
  semantic,
  colors: {
    primary: {
      base: DARK_GREY_BLUE,
      light: GREY_BLUE,
      lighter: LIGHT_GREY_BLUE,
      ice: ICE_BLUE,
      error: ERROR,
      lightError: RED[20],
    },
    secondary: {
      base: ELECTRIC_BLUE,
      light: DARK_SKY_BLUE,
      lighter: LIGHT_SKY_BLUE,
    },
    pastels: {
      green: LIGHT_GREEN,
      red: LIGHT_RED,
      orange: ORANGE[50],
      blue: LIGHT_BLUE,
    },
    redesign: {
      b5: BLUE[5],
      b10: BLUE[10],
      b15: BLUE[15],
      b20: BLUE[20],
      b30: BLUE[30],
      b40: BLUE[40],
      b50: BLUE[50],
      b60: BLUE[60],
      b70: BLUE[70],
      b80: BLUE[80],
      b90: BLUE[90],
      b100: BLUE[100],
      db1: DARK_BLUE[1],
      db5: DARK_BLUE[5],
      db10: DARK_BLUE[10],
      db20: DARK_BLUE[20],
      db30: DARK_BLUE[30],
      db40: DARK_BLUE[40],
      db50: DARK_BLUE[50],
      db60: DARK_BLUE[60],
      db70: DARK_BLUE[70],
      db80: DARK_BLUE[80],
      db90: DARK_BLUE[90],
      db100: DARK_BLUE[100],
      g10: GREEN[10],
      g20: GREEN[20],
      g30: GREEN[30],
      g40: GREEN[40],
      g50: GREEN[50],
      g60: GREEN[60],
      g70: GREEN[70],
      g80: GREEN[80],
      g90: GREEN[90],
      g100: GREEN[100],
      o10: ORANGE[10],
      o20: ORANGE[20],
      o30: ORANGE[30],
      o40: ORANGE[40],
      o50: ORANGE[50],
      o60: ORANGE[60],
      o70: ORANGE[70],
      o80: ORANGE[80],
      o90: ORANGE[90],
      o100: ORANGE[100],
      p10: PURPLE[10],
      p20: PURPLE[20],
      p30: PURPLE[30],
      p40: PURPLE[40],
      p50: PURPLE[50],
      p60: PURPLE[60],
      p70: PURPLE[70],
      p80: PURPLE[80],
      p90: PURPLE[90],
      p100: PURPLE[100],
      r10: RED[10],
      r20: RED[20],
      r30: RED[30],
      r40: RED[40],
      r50: RED[50],
      r60: RED[60],
      r70: RED[70],
      r80: RED[80],
      r90: RED[90],
      r100: RED[100],
      t10: TEAL[10],
      t20: TEAL[20],
      t30: TEAL[30],
      t40: TEAL[40],
      t50: TEAL[50],
      t60: TEAL[60],
      t70: TEAL[70],
      t80: TEAL[80],
      t90: TEAL[90],
      t100: TEAL[100],
      y10: YELLOW[10],
      y20: YELLOW[20],
      y30: YELLOW[30],
      y40: YELLOW[40],
      y50: YELLOW[50],
      y60: YELLOW[60],
      y70: YELLOW[70],
      y80: YELLOW[80],
      y90: YELLOW[90],
      y100: YELLOW[100],
      disabled_grey: DISABLED_GREY,
    },
    activity: {
      background: BLUE[10],
      accent: BLUE[80],
    },
    exercise: {
      background: ORANGE[10],
      accent: ORANGE[80],
    },
    functionality: {
      background: YELLOW[10],
      accent: YELLOW[90],
    },
    lesson: {
      background: TEAL[30],
      accent: TEAL[90],
    },
    report: {
      background: PURPLE[10],
      accent: PURPLE[80],
    },
    payment: {
      background: BLUE[10],
      accent: BLUE[80],
    },
    update_payment_method: {
      background: BLUE[10],
      accent: BLUE[80],
    },
    weekly_report_and_health: {
      background: PURPLE[20],
      accent: DARK_BLUE[80],
    },
    green: FEELING_GREEN,
    orange: ORANGE[100],
    brightOrange: ORANGE[20],
    white: WHITE,
    black: BLACK,
    tomato: TOMATO,
    salmon: SALMON,
    painful_blue: PAINFUL_BLUE,
    confirmation_green: GREEN[20],
    graph: {
      goal: GREEN[80],
      pain: PURPLE[80],
      function: YELLOW[100],
      functionText: "#E7BD2F",
    },
    greens: {
      g70: GREEN[70],
    },
    greys: {
      dark: SLATE_GREY,
      darker: DARKER_GREY,
      pinkish: PINKISH_GREY,
      silver: SILVER,
      light1: LIGHT_GREY1,
      light2: LIGHT_GREY2,
      light3: LIGHT_GREY3,
      light4: LIGHT_GREY4,
      light5: LIGHT_GREY5,
      light6: LIGHT_GREY6,
      light7: LIGHT_GREY7,
      warm: WARM_GREY,
      slate: SLATE_GREY,
      iron: IRON_GREY,
      concrete: CONCRETE_GREY,
      gunmetal: GUNMETAL_GREY,
      bluish: DARK_BLUE[70],
      newGrey: NEW_GREY,
      lightestGrey: LIGHTEST_GREY,
    },
    avatar_colors: COLOR_SWATCHES,
    voice_call_container: VOICE_CALL_CONTAINER,
    broadcast: {
      background: TEAL[30],
      hover: TEAL[20],
      accent: TEAL[90],
    },
    pain_location_colors: PAIN_LOCATION_COLORS,
    table_border_highlight: BLUE[80],
    system_suggestions_section: {
      background: TEAL[10],
      header_background: TEAL[20],
      divider: TEAL[50],
    },
    treatmentSwitching: {
      current: GREEN[30],
      previous: ORANGE[30],
      current_bg: LIGHT_GREY4,
      previous_bg: ORANGE[10],
    },
  },
  components: {
    calendar: {
      bg: WHITE,
      btn_bg: BLUE[100],
      btn_bg_hover: BLUE[90],
      btn_color: WHITE,
      daygrid_bg_today: BLUE[10],
      timegrid_bg_today: WHITE,
      col_header_bg: "none",
      col_header_bg_today: BLUE[100],
      col_header_color: BLACK,
      col_header_color_today: WHITE,
      toolbar_title_color: DARK_BLUE[70],
      appointments: {
        digital_kick_off: BLUE[30],
        digital_kick_off_border: BLUE[50],
        digital_kick_off_availability: BLUE[10],
        physical_kick_off: ORANGE[30],
        physical_kick_off_border: ORANGE[50],
        physical_kick_off_availability: ORANGE[10],
        digital_follow_up: GREEN[30],
        digital_follow_up_border: GREEN[50],
        digital_follow_up_availability: GREEN[10],
        physical_follow_up: YELLOW[30],
        physical_follow_up_border: YELLOW[50],
        physical_follow_up_availability: YELLOW[10],
        group: PURPLE[20],
        group_border: PURPLE[40],
        phone: DARK_BLUE[30],
        phone_border: DARK_BLUE[50],
        phone_availability: DARK_BLUE[10],
        blocked: RED[40],
        blocked_border: RED[60],
        canceled: DARK_BLUE[5],
        canceled_border: DARK_BLUE[20],
        default: BLUE[20],
        default_border: BLUE[40],
      },
    },
  },
  spacing: {
    S_5: "5px",
    S_10: "10px",
    S_15: "15px",
    S_20: "20px",
    S_25: "25px",
    S_30: "30px",
    S_40: "40px",
    S_45: "45px",
    S_50: "50px",
    S_60: "60px",
    S_70: "70px",
    S_80: "80px",
    S_120: "120px",
    S_180: "180px",
  },
  borderRadius: {
    basic: "8px",
    circular: "50%",
    library: "12px",
  },
  font: {
    display1: {
      "line-height": "56px",
      "font-size": "48px",
      "font-weight": FONT_WEIGHT_MEDIUM,
    },
    display2: {
      "line-height": "48px",
      "font-size": "40px",
      "font-weight": FONT_WEIGHT_MEDIUM,
    },
    header1: {
      "line-height": "36px",
      "font-size": "30px",
      "font-weight": FONT_WEIGHT_MEDIUM,
    },
    header2: {
      "line-height": "32px",
      "font-size": "24px",
      "font-weight": FONT_WEIGHT_REGULAR,
    },
    header3: {
      "line-height": "30px",
      "font-size": "22px",
      "font-weight": FONT_WEIGHT_MEDIUM,
    },
    header4: {
      "line-height": "26px",
      "font-size": "18px",
      "font-weight": FONT_WEIGHT_REGULAR,
    },
    header5: {
      "line-height": "26px",
      "font-size": "18px",
      "font-weight": FONT_WEIGHT_REGULAR,
    },
    body1: {
      "line-height": "25px",
      "font-size": "18px",
      "font-weight": FONT_WEIGHT_LIGHT,
    },
    body2: {
      "line-height": "23px",
      "font-size": "16px",
      "font-weight": FONT_WEIGHT_LIGHT,
    },
    link1: {
      "line-height": "26px",
      "font-size": "18px",
      "font-weight": FONT_WEIGHT_MEDIUM,
    },
    link2: {
      "line-height": "26px",
      "font-size": "16px",
      "font-weight": FONT_WEIGHT_MEDIUM,
    },
    link3: {
      "line-height": "26px",
      "font-size": "14px",
      "font-weight": FONT_WEIGHT_MEDIUM,
    },
    caption: {
      "line-height": "22px",
      "font-size": "14px",
      "font-weight": FONT_WEIGHT_LIGHT,
    },
    caption2: {
      "line-height": "20px",
      "font-size": "12px",
      "font-weight": FONT_WEIGHT_LIGHT,
    },
    caption3: {
      "line-height": "18px",
      "font-size": "10px",
      "font-weight": FONT_WEIGHT_LIGHT,
    },
    info: {
      "line-height": "26px",
      "font-size": "16px",
      "font-weight": FONT_WEIGHT_LIGHT,
      color: SLATE_GREY,
    },
    app_version: {
      color: DARK_BLUE[80],
      "font-size": "12px",
      "font-weight": FONT_WEIGHT_REGULAR,
      "letter-spacing": "0.25px",
      "line-height": "14px",
    },
    sub_menu: {
      link: {
        "line-height": "20px",
        "font-size": "14px",
        "font-weight": FONT_WEIGHT_MEDIUM,
      },
      title: {
        "line-height": "32px",
        "font-size": "14px",
        "font-weight": FONT_WEIGHT_MEDIUM,
      },
    },
    modal: {
      title: {
        "line-height": "46px",
        "font-size": "18px",
        "font-weight": FONT_WEIGHT_REGULAR,
        color: DARK_GREY_BLUE,
      },
    },
    table: {
      sort: {
        "line-height": "40px",
        "font-size": "14px",
        "font-weight": FONT_WEIGHT_MEDIUM,
        color: WARM_GREY,
      },
      header: {
        "line-height": "48px",
        "font-size": "16px",
        "font-weight": FONT_WEIGHT_REGULAR,
        color: SLATE_GREY,
      },
      row: {
        "line-height": "48px",
        "font-size": "14px",
        "font-weight": FONT_WEIGHT_REGULAR,
        color: SLATE_GREY,
      },
    },
    small_caps_title: {
      "line-height": "24px",
      "font-size": "20px",
      "font-weight": FONT_WEIGHT_BOLD,
      "letter-spacing": "1px",
      "font-variant": "small-caps",
    },
    button_text: {
      "line-height": "20px",
      "font-size": "14px",
      "font-weight": FONT_WEIGHT_BOLD,
      "letter-spacing": "1px",
      "text-transform": "uppercase",
    },
    section_title: {
      "line-height": "20px",
      "font-size": "14px",
      "font-weight": FONT_WEIGHT_MEDIUM,
      "letter-spacing": "1.25px",
      "text-transform": "uppercase",
      color: DARK_BLUE[60],
    },
    autosave_label: {
      "line-height": "19px",
      "font-size": "16px",
      "font-weight": FONT_WEIGHT_REGULAR,
      color: DARK_BLUE[60],
    },
    input: {
      label: {
        "font-size": "14px",
        "font-weight": FONT_WEIGHT_REGULAR,
      },
      label_standalone: {
        "font-size": "16px",
        "font-weight": FONT_WEIGHT_REGULAR,
        "line-height": "18px",
      },
      field: {
        "font-size": "16px",
        "line-height": "22px",
        "font-weight": FONT_WEIGHT_REGULAR,
      },
      error: {
        "font-size": "11px",
        "line-height": "15px",
        "font-weight": FONT_WEIGHT_REGULAR,
      },
    },
    message: {
      therapist_body: {
        "font-size": "16px",
        "line-height": "24px",
        "font-weight": FONT_WEIGHT_REGULAR,
        "letter-spacing": "0.5px",
      },
      patient_body: {
        "font-size": "16px",
        "line-height": "24px",
        "font-weight": FONT_WEIGHT_REGULAR,
        "letter-spacing": "0.5px",
      },
      date: {
        "font-size": "14px",
        "line-height": "20px",
        "letter-spacing": "0.5px",
      },
    },
    system_suggestions_section: {
      header: {
        color: DARK_BLUE[100],
        "font-size": "12px",
        "font-weight": FONT_WEIGHT_BOLD,
        "letter-spacing": "0.5px",
        "line-height": "20px",
        "text-transform": "uppercase",
      },
      label: {
        color: DARK_BLUE[100],
        "font-size": "12px",
        "font-weight": FONT_WEIGHT_BOLD,
        "letter-spacing": "0.5px",
      },
      body: {
        color: DARK_BLUE[100],
        "font-size": "12px",
        "font-weight": FONT_WEIGHT_REGULAR,
        "letter-spacing": "0.5px",
        "line-height": "20px",
        "white-space": "pre-line",
      },
    },
  },
  fontWeight: {
    light: FONT_WEIGHT_LIGHT,
    regular: FONT_WEIGHT_REGULAR,
    medium: FONT_WEIGHT_MEDIUM,
    bold: FONT_WEIGHT_BOLD,
  },
  breakpoint: BREAKPOINT,
  breakpointMobile: BREAKPOINT_MOBILE,
  breakpointLarge: BREAKPOINT_LARGE,
  breakpointLib: BREAKPOINT_LIB,
  belowBreakpoint: `@media screen and (max-width: ${BREAKPOINT}px)`,
  aboveBreakpoint: `@media screen and (min-width: ${BREAKPOINT}px)`,
  belowMobileBreakpoint: `@media screen and (max-width: ${BREAKPOINT_MOBILE}px)`,
  aboveMobileBreakpoint: `@media screen and (min-width: ${BREAKPOINT_MOBILE}px)`,
  belowLargeBreakpoint: `@media screen and (max-width: ${BREAKPOINT_LARGE}px)`,
  aboveLargeBreakpoint: `@media screen and (min-width: ${BREAKPOINT_LARGE}px)`,
  belowLibBreakpoint: `@media screen and (max-width: ${BREAKPOINT_LIB}px)`,
  aboveLibBreakpoint: `@media screen and (min-width: ${BREAKPOINT_LIB}px)`,
  containerWidth: 1020,
  videoCallWidthMaximised: 480,
  videoCallWidthMinimised: 240,
  zIndex: {
    bioPreview: 1002,
    videoCall: 1300,
    videoCallMobile: 1010,
    modal: 997,
    headerMobile: 1001,
    header: 1000,
    headerDropdown: 999,
    headerDropdownOverlay: 998,
    belowMessage: 850,
    messageInput: 900,
    aboveMessage: 950,
    tooltip: 980,
    info: 500,
    overlay: 9999,
  },
};
